import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Camera, FileText, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";

function isPdfFile(file: File): boolean {
  return file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
}

function isImageFile(file: File): boolean {
  return file.type.startsWith("image/") || /\.(png|jpe?g|webp)$/i.test(file.name);
}

export function InboundPackingSlipConfirmModal({
  open,
  file,
  pageLabel,
  confirming = false,
  onConfirm,
  onConfirmAndAddAnother,
  onTryAgain,
  onCancel,
}: {
  open: boolean;
  file: File | null;
  pageLabel?: string | null;
  confirming?: boolean;
  onConfirm: () => void;
  onConfirmAndAddAnother?: () => void;
  onTryAgain?: () => void;
  onCancel: () => void;
}): JSX.Element | null {
  const previewUrl = useMemo(() => {
    if (!open || file == null) return null;
    return URL.createObjectURL(file);
  }, [file, open]);

  useEffect(() => {
    return () => {
      if (previewUrl != null) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  if (!open || file == null) return null;

  const pdf = isPdfFile(file);
  const image = isImageFile(file);
  const showAddAnother = onConfirmAndAddAnother != null;

  return createPortal(
    <div {...hubModalOverlayProps(true)}>
      <Card className="mt-6 w-full max-w-3xl shadow-xl">
        <CardHeader className="border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">
            {pageLabel != null && pageLabel.trim() !== "" ? `Confirm packing slip (${pageLabel})` : "Confirm packing slip"}
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            Review this page before saving. Multi-page slips need one photo or file per page — save each page, then
            capture or upload the rest.
          </p>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
            {image && previewUrl != null ? (
              <img
                src={previewUrl}
                alt="Packing slip preview"
                className="mx-auto max-h-[min(60vh,28rem)] w-full object-contain"
              />
            ) : pdf && previewUrl != null ? (
              <iframe
                title="Packing slip PDF preview"
                src={previewUrl}
                className="h-[min(60vh,28rem)] w-full bg-white"
              />
            ) : (
              <div className="flex items-center gap-3 px-4 py-8 text-sm text-slate-600">
                <FileText className="h-8 w-8 shrink-0 text-slate-400" aria-hidden />
                <div>
                  <p className="font-medium text-slate-900">{file.name}</p>
                  <p>Preview is not available for this file type. Confirm only if you selected the correct packing slip.</p>
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" disabled={confirming} onClick={onCancel}>
              Cancel
            </Button>
            {onTryAgain != null ? (
              <Button type="button" variant="secondary" disabled={confirming} onClick={onTryAgain}>
                <Camera className="h-4 w-4" aria-hidden />
                Try again
              </Button>
            ) : null}
            {showAddAnother ? (
              <Button
                type="button"
                variant="secondary"
                disabled={confirming}
                onClick={onConfirmAndAddAnother}
              >
                {confirming ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Camera className="h-4 w-4" aria-hidden />}
                Save page and capture another
              </Button>
            ) : null}
            <Button type="button" variant="primary" disabled={confirming} onClick={onConfirm}>
              {confirming ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
              Save page
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>,
    document.body,
  );
}
